import { useEffect, useMemo, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import {
  findWindowsTimezoneOption,
  getTimezoneOptionsForPicker,
} from "../utils/windowsTimezoneOptions";

export default function TimezoneSelect({
  value,
  onChange,
  disabled = false,
  className = "",
  placeholder = "Search timezones...",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const selected = findWindowsTimezoneOption(value);
  const allOptions = useMemo(
    () => getTimezoneOptionsForPicker(value),
    [value],
  );

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((opt) => opt.searchText.includes(q));
  }, [allOptions, search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const displayLabel = selected
    ? selected.label
    : value
      ? value
      : "";

  const openPicker = () => {
    if (disabled) return;
    setSearch("");
    setIsOpen(true);
  };

  const handleSelect = (identifier) => {
    onChange(identifier);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      <div
        className={`flex items-center gap-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white ${
          disabled ? "bg-gray-100 cursor-not-allowed opacity-60" : "cursor-text"
        }`}
        onClick={openPicker}
      >
        <div className="flex-1 min-w-0">
          {isOpen ? (
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder}
              className="w-full bg-transparent border-0 p-0 text-sm focus:outline-none"
              disabled={disabled}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="min-w-0">
              <div className="truncate text-gray-900">
                {displayLabel || placeholder}
              </div>
              {selected?.standardName ? (
                <div className="truncate text-xs text-gray-500">
                  {selected.standardName}
                </div>
              ) : null}
            </div>
          )}
        </div>
        <FaChevronDown
          className={`w-3 h-3 text-gray-500 shrink-0 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {isOpen && !disabled ? (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">No timezones found</div>
          ) : (
            filteredOptions.map((opt) => (
              <button
                key={opt.identifier}
                type="button"
                onClick={() => handleSelect(opt.identifier)}
                className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-50 last:border-b-0 ${
                  opt.identifier === value ? "bg-red-50" : ""
                }`}
              >
                <div className="text-sm text-gray-900">{opt.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{opt.standardName}</div>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
